import { FC, useEffect, useState } from "react";

import { Translate } from "@/app/application/Translate";
import { ProcedureNodeStep } from "@/features/procedures/model/types/checklist";
import { CheckpointType } from "@/features/procedures/model/types/manualChecklist";
import { ProcedureButton } from "@/features/procedures/ui/components/common/ProcedureButton";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import SensorsIcon from "@mui/icons-material/Sensors";
import { Badge, SxProps, Tooltip } from "@mui/material";

interface LiveStepStatusProps {
	step: ProcedureNodeStep;
	toggleShowMeasurements: () => void;
	isActiveStep: boolean;
}

export const LiveStepStatus: FC<LiveStepStatusProps> = ({
	step,
	toggleShowMeasurements,
	isActiveStep,
}) => {
	const defaultStyle = {
		backgroundColor: "grey",
		color: "white",
		minWidth: "40px",
		maxWidth: "40px",
		height: "40px",
	};
	const [buttonStyling, setButtonStyling] = useState<SxProps>(defaultStyle);

	const overridenStyles = {
		backgroundColor: "rgba(0, 0, 0, 0.12)",
		color: "white",
		minWidth: "40px",
		maxWidth: "40px",
		height: "40px",
	};

	const completedStyles = {
		minWidth: "40px",
		backgroundColor: "green",
		color: "white",
		maxWidth: "40px",
		height: "40px",
		"&:hover": {
			backgroundColor: "white",
			color: "green",
		},
	};

	const errorStyles = {
		backgroundColor: "red",
		color: "white",
		minWidth: "40px",
		maxWidth: "40px",
		height: "40px",
		"&:hover": {
			backgroundColor: "white",
			color: "red",
		},
	};

	const waitingStyles = {
		backgroundColor: "yellow",
		color: "grey",
		minWidth: "40px",
		maxWidth: "40px",
		height: "40px",
		animation: "pulseButton 2s linear infinite",
		"@keyframes pulseButton": {
			"0%": {
				backgroundColor: "grey",
				color: "white",
			},
			"50%": {
				backgroundColor: "yellow",
			},
			"100%": {
				backgroundColor: "grey",
				color: "white",
			},
		},
	};

	useEffect(() => {
		if (step.IsSkipped || step.IsOverridden) {
			setButtonStyling(overridenStyles);
			return;
		} else if (step.Status) {
			setButtonStyling(completedStyles);
			return;
		} else if (!isActiveStep && !step.Status) {
			setButtonStyling(defaultStyle);
			return;
		} else if (
			isActiveStep &&
			!step.Status &&
			step.ListenerTags.some((x) => x.CurrentValue === null)
		) {
			setButtonStyling(errorStyles);
			return;
		} else if (isActiveStep && !step.Status) {
			setButtonStyling(waitingStyles);
		}
	}, [isActiveStep, step.ListenerTags, step.Status]);

	if (step.CheckpointType !== CheckpointType.SATOSLive) {
		return <></>;
	}

	return (
		<Badge
			badgeContent={
				<Tooltip
					placement="top"
					title={
						<Translate String="Live.Step.No.Listener">No listenertag</Translate>
					}
				>
					<ReportProblemIcon sx={{ fontSize: "10px" }} />
				</Tooltip>
			}
			color="warning"
			invisible={!!step.ListenerTags.length}
			sx={{ cursor: "help" }}
			anchorOrigin={{
				vertical: "top",
				horizontal: "left",
			}}
		>
			<Tooltip
				title={
					step.IsSkipped ? (
						<Translate String="Live.Step.Skipped">Step is skipped</Translate>
					) : (
						<Translate String="Live.Step.ShowListener">
							Show listeners
						</Translate>
					)
				}
			>
				<ProcedureButton
					disabled={step.IsSkipped}
					sx={buttonStyling}
					type="button"
					variant="contained"
					onClick={() => toggleShowMeasurements()}
				>
					<SensorsIcon />
				</ProcedureButton>
			</Tooltip>
		</Badge>
	);
};
